import { useEffect, useState } from 'react';
import { EngagementQueue } from './services/EngagementQueue';

type RegistryListenerQueueId = string;

type RegistryListenerCallback = (queue: EngagementQueue | undefined) => void;

type RegistryListener = {
    queueId: RegistryListenerQueueId;
    callback: RegistryListenerCallback;
};

class QueueRegistry extends Map<string, EngagementQueue> {
    private listeners: RegistryListener[] = [];

    constructor() {
        super();
    }

    subscribe(
        queueId: RegistryListenerQueueId,
        callback: RegistryListenerCallback
    ) {
        this.listeners.push({ queueId, callback });
    }

    unsubscribe(callback: RegistryListenerCallback) {
        this.listeners = this.listeners.filter(
            (listener) => listener.callback !== callback
        );
    }

    // overload methods that change the map so we can notify listeners
    set(key: string, value: EngagementQueue) {
        this.listeners.forEach((listener) => {
            if (listener.queueId === key) {
                listener.callback(value);
            }
        });
        return super.set(key, value);
    }

    clear() {
        this.listeners.forEach((listener) => {
            listener.callback(undefined);
        });
        return super.clear();
    }

    delete(key: string): boolean {
        this.listeners.forEach((listener) => {
            if (listener.queueId === key) {
                listener.callback(undefined);
            }
        });
        return super.delete(key);
    }

    // all the read-only methods are used as-is
}

const registry = new QueueRegistry();

export const createEngagementQueue = (queueId: string, storage: Storage) => {
    registry.set(queueId, new EngagementQueue(storage, queueId));
};

export const getEngagementQueue = (queueId: string) => {
    return registry.get(queueId);
};

// mostly for testing
export const deleteAllEngagementQueues = () => {
    registry.clear();
};

export const useEngagementQueue = (queueId: string) => {
    const [queue, setQueue] = useState<EngagementQueue | undefined>(
        getEngagementQueue(queueId)
    );

    useEffect(() => {
        // subscribe to changes to the queue
        const listener = (queue: EngagementQueue | undefined) => {
            setQueue(queue);
        };
        registry.subscribe(queueId, listener);

        // attach to the queue if it was created between component mount and right now
        const queue = getEngagementQueue(queueId);
        if (queue) {
            setQueue(queue);
        }

        // unsubscribe from changes to the queue if the component unmounts
        return () => {
            registry.unsubscribe(listener);
        };
    }, [queueId]);

    return {
        queue,
        hasQueue: queue !== undefined,
    };
};
